import { useEffect, useState } from "react";
import styled from "styled-components";
import { GetFAQData } from "../Api/callData.js";
import { DataNotFound } from "../Api/dataNotFound.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 580px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const Tab = styled.a`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Content ── */
const ContentSection = styled.section`
  padding: clamp(40px, 5vw, 64px) 0 clamp(80px, 10vw, 120px);
`;

const AdminBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 999px;
    background: #0f172a;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.15s ease;
  }
  a:hover {
    background: #1e293b;
  }
`;

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
  }

  &.open {
    border-color: #c7d2fe;
    box-shadow: 0 12px 28px -16px rgba(99, 102, 241, 0.25);
  }
`;

const Question = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 24px;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.5;

  .badge {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #7c3aed);
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0;
  }

  .text {
    flex: 1;
  }

  .chevron {
    color: #94a3b8;
    transition: transform 0.2s ease, color 0.15s ease;
  }

  &[aria-expanded="true"] .chevron {
    transform: rotate(180deg);
    color: #6366f1;
  }
`;

const Answer = styled.div`
  padding: 0 24px 22px 70px;
  font-size: 15px;
  line-height: 1.75;
  color: #475569;
  white-space: pre-line;

  @media (max-width: 600px) {
    padding-left: 24px;
  }
`;

/* ── Pagination ── */
const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 48px;
`;

const PageBtn = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "#ffffff"};
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$active ? "transparent" : "#cbd5e1")};
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── States ── */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 15px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ── Icons ── */
const ChevronDown = () => (
  <svg className="chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const ChevronsLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 18l-6-6 6-6M19 18l-6-6 6-6" />
  </svg>
);
const ChevronsRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13 18l6-6-6-6M5 18l6-6-6-6" />
  </svg>
);

const PAGE_SIZE = 5;

export function Faq() {
  const [post, setPost] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const [nowPath, setNowPath] = useState("");

  useEffect(() => {
    const lastSegment = window.location.pathname.split("/").pop();
    setNowPath(lastSegment);
  }, []);

  const loadFAQ = async (page) => {
    setLoading(true);
    const { postsData, lastVisibleDoc, totalSize } = await GetFAQData(
      page,
      lastVisible
    );
    setPost(postsData);
    setLastVisible(lastVisibleDoc);
    setLoading(false);
    setTotalPosts(totalSize);
  };

  useEffect(() => {
    loadFAQ(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil((totalPosts || 0) / PAGE_SIZE));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setActiveIndex(null);
  };

  const isLoggedIn = typeof window !== "undefined" && localStorage.length > 0;

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Support</Eyebrow>
          <HeroTitle>FAQ</HeroTitle>
          <HeroLead>
            Quick answers to common questions about Youngand and Brain Health
            Playground.
          </HeroLead>
          <Tabs>
            <Tab href="/support/faq" $active>
              FAQ
            </Tab>
            <Tab href="/support/inquiry">Inquiry</Tab>
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          {isLoggedIn && (
            <AdminBar>
              <a href="/adminWrite">Write a post</a>
            </AdminBar>
          )}

          {loading ? (
            <StateBox>
              <Spinner />
              <div>Loading…</div>
            </StateBox>
          ) : post && post.length > 0 ? (
            <>
              <Accordion>
                {post.map((p, idx) => (
                  <Item
                    key={p.id}
                    className={activeIndex === idx ? "open" : ""}
                  >
                    <Question
                      type="button"
                      aria-expanded={activeIndex === idx}
                      onClick={() =>
                        setActiveIndex(activeIndex === idx ? null : idx)
                      }
                    >
                      <span className="badge">Q</span>
                      <span className="text">{p.title}</span>
                      <ChevronDown />
                    </Question>
                    {activeIndex === idx && <Answer>{p.content}</Answer>}
                  </Item>
                ))}
              </Accordion>

              <Pagination aria-label="Pagination">
                <PageBtn
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="First page"
                >
                  <ChevronsLeftIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon />
                </PageBtn>
                {[...Array(totalPages)].map((_, i) => (
                  <PageBtn
                    key={i}
                    $active={currentPage === i + 1}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </PageBtn>
                ))}
                <PageBtn
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Next page"
                >
                  <ChevronRightIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Last page"
                >
                  <ChevronsRightIcon />
                </PageBtn>
              </Pagination>
            </>
          ) : (
            <DataNotFound table={nowPath} />
          )}
        </Container>
      </ContentSection>
    </Page>
  );
}
